import { WRITE_SKILL_ID, writeCardLabel } from '@/utils/write-skill.util';
import { branchRouteSummary } from './node-arms';
import {
  extendedBranchConditions,
  summarizeBranchConditions,
} from './branch-conditions';
import { syncOwnerWords } from './config/sync-owner-config.util';
import { crmWriteSummary, isCrmWriteAction } from './config/crm-write-config';
import { dealConfig, isDealKind } from './config/deal-trigger.util';
import { SEQUENCE_EVENT_OPTIONS } from './sequence-events';
import { hasCrmChangeConditions } from './config/crm-change-conditions';
import { browserTimezone } from '@/components/builder/timezone.util';
import { agentsCostPerRecord } from '@/containers/agents/agent-prices';
import {
  defaultBriefSchedule,
  type BriefKind,
} from '@/containers/agents/brief.util';
import { formatClock } from '@/components/builder/clock.util';
import { zoneCity } from '@/components/builder/timezone.util';
import { format, isValid, parse } from 'date-fns';
import type { AgentPrices } from '@/types';
import {
  enrichmentCostPerPerson,
  validationCostPerPerson,
} from '@/types/crm-enrichment-playbook';
import { INITIAL_FILTERS } from '@/types/search-filters';
import { COUNTRIES } from '@/constants/countries';
import { INITIAL_COMPANY_FILTERS } from '@/utils/company-search.utils';
import type {
  AgentTaskConfig,
  AgentTaskTool,
  ApprovalConfig,
  AddToSequenceConfig,
  CreateGoogleDocConfig,
  SendMailboxEmailConfig,
  SendLinkedInActionConfig,
  ExportConfig,
  FindMeetingsConfig,
  JobHistoryConfig,
  IdentifyPersonConfig,
  MatchCompanyConfig,
  CountPeopleConfig,
  InstantlyCapacityConfig,
  CustomNodeConfig,
  CustomNodeType,
  BranchConfig,
  CrmActionConfig,
  EnrichConfig,
  CompanyFieldConfig,
  FindPeopleConfig,
  GetCompanyConfig,
  IncomingWebhookConfig,
  JobFilters,
  RecordKind,
  MergeDuplicatesConfig,
  NotifyConfig,
  ResearchConfig,
  ReviewDuplicatesConfig,
  TriggerConfig,
  TriggerKind,
  TriggerSchedule,
  ValidateConfig,
  WebhookConfig,
  WaitConfig,
  CodeConfig,
  HandOffConfig,
  CallNotesConfig,
  NotionStepConfig,
  PlayCrm,
} from '@/types/custom-playbooks';
import {
  isPlayCrm,
  PLAY_CRM_NAMES,
  triggerKind,
} from '@/types/custom-playbooks';
import {
  defaultNotionStepConfig,
  notionStepSummary,
  notionTriggerSummary,
} from './notion/notion.util';
import { LAST_DAY_OF_MONTH, ordinal } from './month-day';
import {
  CLEANUP_CRM_TYPES,
  reviewPolicyLabel,
  type CleanupSeed,
  cleanupScopeWords,
  cleanupTriggerConfig,
  defaultReviewConfig,
  mergeConfigFor,
  mergeFieldsWords,
} from './cleanup-rules';
import { sequenceStepSummary } from './sequence-step.util';
import { connectorDetail, connectorHeadline } from './connectors';
import { callNotesSummary } from './call-trigger';
import { recordWindows } from './trigger-record-windows';
import { webhookHost } from './config/webhook-config.util';
import { incomingWebhookPreset } from './config/incoming-webhook.util';
import { waitSummary } from './config/wait-config.util';
import {
  createGoogleDocSummary,
  googleDocTriggerSummary,
} from './config/google-doc.util';
import { sendMailboxEmailSummary } from './config/mailbox-email.util';
import { sendLinkedInActionSummary } from './config/linkedin-action.util';
import {
  JOB_HISTORY_DEFAULT_CONFIG,
  jobHistorySummary,
} from './config/job-history.util';
import {
  IDENTIFY_PERSON_DEFAULT_CONFIG,
  identifyPersonSummary,
} from './config/identify-person.util';
import {
  MATCH_COMPANY_DEFAULT_CONFIG,
  matchCompanySummary,
} from './config/match-company.util';
import {
  SELF_AGENT_ID,
  hasSelf,
  researchCreditsPerRecord,
  selfSummary,
} from './config/agent-self.util';
import { BRAND } from '@/lib/brand';

/**
 * Per-type builder metadata for the Custom Playbook Builder (build-v2-plan
 * §4.3 — the FINAL 8 node types). LEAF module — NO React imports. The component
 * layer (`node-catalog.tsx`) wraps each entry with its icon and `ConfigBody`;
 * config bodies import THIS without ever touching the component map.
 *
 * Cost math imports the credit SSOTs from `crm-enrichment-playbook.ts` — never
 * re-derives a price locally.
 */

/** Header category — drives the node card's colour strip + label. */
export type NodeCategory =
  | 'SOURCE'
  | 'ENRICH'
  | 'AI'
  | 'LOGIC'
  | 'CRM'
  | 'OUTPUT';

/** Static record-kind constraint hint. `'any'` = kind unchanged / either kind
 *  accepted; the config-aware refinement (enrich target, the find_people flip)
 *  lives in `graph-ops.validateGraph`, mirroring architecture §2.4. A
 *  `duplicate_group` is a cleanup scan's group of CRM records (W5). */
export type KindConstraint = 'company' | 'person' | 'duplicate_group' | 'any';

/** Category → accent colour (hex). Mirrors the alpha builder's palette so the
 *  canvas reads as the same product. */
export const CATEGORY_COLOR: Record<NodeCategory, string> = {
  SOURCE: '#3E8BFF',
  ENRICH: '#22C55E',
  AI: '#8B5CF6',
  LOGIC: '#F59E0B',
  CRM: '#06B6D4',
  OUTPUT: '#EF4444',
};

/** Maps each node type to its concrete config interface. */
interface ConfigMap {
  trigger: TriggerConfig;
  enrich: EnrichConfig;
  find_open_jobs: CompanyFieldConfig;
  find_reviews: CompanyFieldConfig;
  find_people: FindPeopleConfig;
  get_company: GetCompanyConfig;
  fireflies_notes: CallNotesConfig;
  fathom_notes: CallNotesConfig;
  gong_notes: CallNotesConfig;
  research: ResearchConfig;
  validate: ValidateConfig;
  add_to_sequence: AddToSequenceConfig;
  export: ExportConfig;
  create_google_doc: CreateGoogleDocConfig;
  send_mailbox_email: SendMailboxEmailConfig;
  send_linkedin_action: SendLinkedInActionConfig;
  crm_action: CrmActionConfig;
  branch: BranchConfig;
  approval: ApprovalConfig;
  notify: NotifyConfig;
  webhook: WebhookConfig;
  wait: WaitConfig;
  code: CodeConfig;
  run_play: HandOffConfig;
  find_meetings: FindMeetingsConfig;
  job_history: JobHistoryConfig;
  review_duplicates: ReviewDuplicatesConfig;
  merge_duplicates: MergeDuplicatesConfig;
  notion_add_page: NotionStepConfig;
  notion_update_page: NotionStepConfig;
  identify_person: IdentifyPersonConfig;
  match_company: MatchCompanyConfig;
  count_people: CountPeopleConfig;
  instantly_capacity: InstantlyCapacityConfig;
  agent_task: AgentTaskConfig;
}

export interface NodeMeta<C extends CustomNodeConfig = CustomNodeConfig> {
  type: CustomNodeType;
  label: string;
  description: string;
  category: NodeCategory;
  /** Canonical starting config. Always deep-cloned by `nodeDefaultConfig`
   *  before it reaches a node — never share the singleton by reference. */
  defaultConfig: C;
  /** Upper-bound credit cost for `n` records at this node's current config.
   *  Free/structural nodes return 0. Agents are priced per agent from the
   *  org's `agentPrices`; without them the Agents step reads 0 (hidden). */
  costFn: (
    config: C,
    n: number,
    agentPrices?: AgentPrices,
    selfPerRecord?: number
  ) => number;
  /** Record kind accepted on the inbound edge (`null` = root, no input). */
  kindIn: KindConstraint | null;
  /** Record kind emitted on outbound edges. */
  kindOut: KindConstraint;
  /** Branch and Approval split into named arms. */
  isBranching: boolean;
}

type NodeMetadataTable = { [K in CustomNodeType]: NodeMeta<ConfigMap[K]> };

const zero = (): number => 0;

export const NODE_METADATA: NodeMetadataTable = {
  approval: {
    type: 'approval',
    label: 'Approve',
    description: 'Ask a person before records continue',
    category: 'LOGIC',
    defaultConfig: { approvers: { mode: 'play_owner' }, notify: {}, show: [] },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: true,
  },
  run_play: {
    type: 'run_play',
    label: 'Hand off',
    description: 'Send records to another play of this agent',
    category: 'LOGIC',
    defaultConfig: {},
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  trigger: {
    type: 'trigger',
    label: 'Trigger',
    description: 'How records enter this playbook',
    category: 'SOURCE',
    // A fresh trigger has NO source yet — the canvas renders the dashed
    // "Add a Trigger" placeholder until the source picker seeds one (§3.1-6).
    defaultConfig: {} as TriggerConfig,
    costFn: zero,
    kindIn: null,
    kindOut: 'any',
    isBranching: false,
  },
  enrich: {
    type: 'enrich',
    label: 'Enrich',
    description: 'Find emails, phones, and profile data',
    category: 'ENRICH',
    defaultConfig: {
      target: 'person',
      person: { profile: true, email: true, phone: false },
    },
    // Person: per-output enrichment SSOT. Company: flat 1cr/record precheck
    // (architecture §6.5 — "Precheck at claim = 1cr × n").
    costFn: (config, n) =>
      config.target === 'company'
        ? 1 * n
        : enrichmentCostPerPerson(
            config.person ?? { profile: true, email: false, phone: false }
          ) * n,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  find_open_jobs: {
    type: 'find_open_jobs',
    label: 'Find open jobs',
    description: '',
    category: 'ENRICH',
    defaultConfig: {},
    costFn: zero,
    kindIn: 'company',
    kindOut: 'company',
    isBranching: false,
  },
  find_reviews: {
    type: 'find_reviews',
    label: 'Find reviews',
    description: '',
    category: 'ENRICH',
    defaultConfig: {},
    costFn: zero,
    kindIn: 'company',
    kindOut: 'company',
    isBranching: false,
  },
  find_people: {
    type: 'find_people',
    label: 'Find People',
    description: 'Find matching people at each company',
    category: 'SOURCE',
    defaultConfig: {
      audienceFilters: INITIAL_FILTERS,
      audienceCompanyFilters: INITIAL_COMPANY_FILTERS,
      maxPeoplePerAccount: 10,
    },
    // Finding / account lookups are free; the fanned-out people are charged at
    // the downstream enrich node (crm-enrichment cost doc / §6.5).
    costFn: zero,
    kindIn: 'company',
    kindOut: 'person',
    isBranching: false,
  },
  get_company: {
    type: 'get_company',
    label: 'Get company',
    description: 'Turn people into one record per company',
    category: 'SOURCE',
    defaultConfig: { source: 'employer' },
    costFn: zero,
    kindIn: 'person',
    kindOut: 'company',
    isBranching: false,
  },
  fireflies_notes: {
    type: 'fireflies_notes',
    label: 'Get Fireflies call notes',
    description: 'Read the latest Fireflies call: summary and action items',
    category: 'ENRICH',
    defaultConfig: { recency: 'latest' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  fathom_notes: {
    type: 'fathom_notes',
    label: 'Get Fathom call notes',
    description: 'Read the latest Fathom call: summary and action items',
    category: 'ENRICH',
    defaultConfig: { recency: 'latest' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  gong_notes: {
    type: 'gong_notes',
    label: 'Get Gong call notes',
    description: 'Read the latest Gong call: summary and action items',
    category: 'ENRICH',
    defaultConfig: { recency: 'latest' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  research: {
    type: 'research',
    label: 'Agents',
    description: 'Run saved AI agents on each record',
    category: 'AI',
    defaultConfig: { agentIds: [] },
    costFn: (config, n, agentPrices, selfPerRecord) =>
      researchCostPerRecord(config, agentPrices, 'person', selfPerRecord) * n,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  validate: {
    type: 'validate',
    label: 'Validate',
    description: 'Verify email and phone quality',
    category: 'ENRICH',
    defaultConfig: { email: true, phone: false },
    costFn: (config, n) => validationCostPerPerson(config) * n,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // Enrolls people through the sequence's own rules; free. No sequence until
  // one is picked (the validator warns until then).
  add_to_sequence: {
    type: 'add_to_sequence',
    label: 'Add to sequence',
    description: 'Add people to a sequence',
    category: 'OUTPUT',
    defaultConfig: {},
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // One email a person from the member's own Gmail or Outlook mailbox; free.
  // The row's preset sets the provider; no mailbox, subject or body until
  // they are picked and written (the validator warns until then).
  send_mailbox_email: {
    type: 'send_mailbox_email',
    label: 'Send an email',
    description: 'Email each person from your inbox',
    category: 'OUTPUT',
    defaultConfig: { subject: '', body: '' } as SendMailboxEmailConfig,
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // One LinkedIn message or invitation a person from the member's own
  // LinkedIn account; free. No account until one is picked, and a message
  // needs its text (the validator warns until then).
  send_linkedin_action: {
    type: 'send_linkedin_action',
    label: 'Send on LinkedIn',
    description: 'Message or invite each person',
    category: 'OUTPUT',
    defaultConfig: { action: 'message', text: '' },
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // Writes rows to a Google Sheet; free. No sheet until one is picked,
  // pasted or created on the step (the validator warns until then).
  export: {
    type: 'export',
    label: 'Export to Google Sheets',
    description: 'Add or update rows in a Google Sheet',
    category: 'OUTPUT',
    defaultConfig: { destination: 'google_sheets' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  // Google Docs' own step: one doc a record; free. No title or body until
  // they are written (the validator warns until then).
  create_google_doc: {
    type: 'create_google_doc',
    label: 'Create a Google Doc',
    description: 'Create a doc for each record',
    category: 'OUTPUT',
    defaultConfig: { title: '', body: '' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  crm_action: {
    type: 'crm_action',
    label: 'CRM Action',
    description: 'Read CRM data or apply a saved CRM action',
    category: 'CRM',
    defaultConfig: {
      crmType: 'salesforce',
      action: 'sync',
      objectType: 'Contact',
      mappingMode: 'default',
      mappings: [],
      // R10 (build-v3 §2e): `keep` = today's owner behavior; every fresh
      // crm_action carries it explicitly so the mode is never ambiguous.
      assignment: { mode: 'keep' },
    },
    // sync = native upsert, lookup = match_contact — neither spends enrichment
    // credits (§6.5 / §14).
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  branch: {
    type: 'branch',
    label: 'Branch',
    description: 'Route records down different paths',
    category: 'LOGIC',
    // Two arms out of the box: one condition arm + the pinned trailing else.
    // Arm keys are per-node scoped, so fixed literals are safe here.
    defaultConfig: {
      branches: [
        {
          key: 'b1',
          label: 'Branch 1',
          conditions: { operator: 'AND', conditions: [] },
        },
        { key: 'else', label: 'Else', conditions: null },
      ],
    },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: true,
  },
  notify: {
    type: 'notify',
    label: 'Notify',
    description: 'Send a run summary to Slack or email',
    category: 'OUTPUT',
    defaultConfig: { channel: 'slack', channelId: '' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  code: {
    type: 'code',
    label: 'Code',
    description: 'Work out new values from the record with a short script',
    category: 'LOGIC',
    defaultConfig: {
      language: 'javascript',
      source: '',
      inputs: [],
      outputs: null,
    },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  wait: {
    type: 'wait',
    label: 'Wait',
    description: 'Resume each record after a time or on a date',
    category: 'LOGIC',
    defaultConfig: { mode: 'duration', amount: 3, unit: 'days' },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  webhook: {
    type: 'webhook',
    label: 'Webhook',
    description: 'Send records to an https endpoint',
    category: 'OUTPUT',
    defaultConfig: {
      url: '',
      mode: 'per_record',
      batchSize: 100,
      body: null,
      headers: [],
      auth: { type: 'none' },
      outputs: null,
      responseMap: null,
      maxCallsPerPeriod: 1000,
    },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  // Google Calendar, read only and free: the next and the last meeting with
  // each record. No calendar until one is picked (the validator warns).
  find_meetings: {
    type: 'find_meetings',
    label: 'Find meetings',
    description: 'Next and last Google Calendar meeting with each record',
    category: 'ENRICH',
    defaultConfig: {
      googleUserIds: [],
      matchBy: 'contact',
      lookbackDays: 90,
      lookaheadDays: 90,
    },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  // A person's LinkedIn job history from two vendors, agent plays only. It
  // costs one credit for each profile copy it loads from a vendor (up to two
  // a record), a price only the server knows (a copy the run already holds, a
  // miss and an outage are free), so `costFn` is 0 and the card reads the
  // validate response's `costs[id]` (`SERVER_PRICED_STEPS`).
  job_history: {
    type: 'job_history',
    label: 'Job history',
    description: 'Read each person’s LinkedIn job history',
    category: 'ENRICH',
    defaultConfig: { ...JOB_HISTORY_DEFAULT_CONFIG },
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // W5 cleanup steps. Both are free in this build (design section 12) and
  // take only duplicate groups, which pass through them unchanged.
  review_duplicates: {
    type: 'review_duplicates',
    label: 'Review duplicates',
    description: 'Show duplicate groups for review',
    category: 'CRM',
    defaultConfig: defaultReviewConfig(),
    costFn: zero,
    kindIn: 'duplicate_group',
    kindOut: 'any',
    isBranching: false,
  },
  merge_duplicates: {
    type: 'merge_duplicates',
    label: 'Merge duplicates',
    description: 'Merge reviewed groups in your CRM',
    category: 'CRM',
    // A new Merge step takes the trigger's objects (`graph-ops`); this is the
    // Salesforce default for a step made without a graph.
    defaultConfig: mergeConfigFor(['account', 'contact']),
    costFn: zero,
    kindIn: 'duplicate_group',
    kindOut: 'any',
    isBranching: false,
  },
  // Notion's own steps: free, people or companies, an agent's play only.
  notion_add_page: {
    type: 'notion_add_page',
    label: 'Add a page to a Notion database',
    description: 'Add a page for each record',
    category: 'OUTPUT',
    defaultConfig: defaultNotionStepConfig('notion_add_page'),
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  notion_update_page: {
    type: 'notion_update_page',
    label: 'Update a Notion page',
    description: 'Update the matching page for each record',
    category: 'OUTPUT',
    defaultConfig: defaultNotionStepConfig('notion_update_page'),
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  // A person's LinkedIn profile and every candidate weighed, agent plays only.
  // It costs what its lookups cost (a reverse email lookup a provider
  // answered, a person identified: up to two a record), a price only the
  // server knows (a miss, an outage and a rate limit are free), so `costFn` is
  // 0 and the card reads the validate response's `costs[id]`
  // (`SERVER_PRICED_STEPS`).
  identify_person: {
    type: 'identify_person',
    label: 'Identify person',
    description: 'Find each person’s LinkedIn profile',
    category: 'ENRICH',
    defaultConfig: { ...IDENTIFY_PERSON_DEFAULT_CONFIG },
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // The account against each person's job history, agent plays only. Free:
  // it decides from what Identify person and Job history stored.
  match_company: {
    type: 'match_company',
    label: 'Match company',
    description: 'Check the account against each person’s job history',
    category: 'ENRICH',
    defaultConfig: { ...MATCH_COMPANY_DEFAULT_CONFIG },
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // How many people at each person's or company's company match an
  // audience, agent plays only. Free: search reads nothing it charges for.
  // `kindIn` is any: the server takes person and company records.
  count_people: {
    type: 'count_people',
    label: 'Count people',
    description: 'Count matching people at each company',
    category: 'ENRICH',
    defaultConfig: {
      audienceFilters: INITIAL_FILTERS,
      audienceCompanyFilters: INITIAL_COMPANY_FILTERS,
    },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
  // Lets on only as many people as the Instantly inboxes have room for,
  // agent plays only. No settings; the rest wait at the gate. Free.
  instantly_capacity: {
    type: 'instantly_capacity',
    label: 'Instantly capacity',
    description: 'Wait for room in your Instantly inboxes',
    category: 'LOGIC',
    defaultConfig: {},
    costFn: zero,
    kindIn: 'person',
    kindOut: 'person',
    isBranching: false,
  },
  // A scheduled brief's one Clu turn (W2-24). Not in the step picker: the
  // Triggers tab and Clu create briefs. Priced per run, not per record, so
  // `costFn` is 0 and the card shows its credit cap (`BriefCostPill`).
  agent_task: {
    type: 'agent_task',
    label: 'Brief',
    description: 'Write a brief from the agent’s Instructions',
    category: 'AI',
    defaultConfig: { tools: ['knowledge'] },
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  },
};

/** A brief's read tools in their words, in the API's `TASK_TOOLS` order. */
export const AGENT_TASK_TOOLS: Record<AgentTaskTool, string> = {
  crm_read: 'CRM',
  knowledge: 'Knowledge',
  lists_read: 'Lists',
  usage_read: 'Usage',
  run_records: 'Run records',
  web_search: 'Web search',
};

/** A brief's tools as it runs them: its own list, else knowledge. */
export function agentTaskTools(config: AgentTaskConfig): AgentTaskTool[] {
  const tools: readonly string[] = Array.isArray(config.tools)
    ? config.tools
    : ['knowledge'];
  return (Object.keys(AGENT_TASK_TOOLS) as AgentTaskTool[]).filter((tool) =>
    tools.includes(tool)
  );
}

/** Insertion order for the step picker. The picker shows the cleanup steps
 *  only under a `crm_duplicates` trigger, and every other step only under
 *  the rest, each where the cleanup line keeps its order
 *  (`graph-ops.addableStepTypesAt`). */
export const NODE_TYPE_ORDER: CustomNodeType[] = [
  'find_people',
  'get_company',
  'find_open_jobs',
  'find_reviews',
  'fireflies_notes',
  'fathom_notes',
  'gong_notes',
  'enrich',
  'validate',
  'identify_person',
  'research',
  'job_history',
  'match_company',
  'count_people',
  'find_meetings',
  'crm_action',
  'review_duplicates',
  'merge_duplicates',
  'branch',
  'approval',
  'export',
  'create_google_doc',
  'notion_add_page',
  'notion_update_page',
  'instantly_capacity',
  'add_to_sequence',
  'notify',
  'send_mailbox_email',
  'send_linkedin_action',
  'webhook',
  'wait',
  'code',
  'run_play',
];

/**
 * A type this build can show and edit: the §4 eight and the two W5 cleanup
 * steps, every key of `NODE_METADATA`. A saved play or proposal holding any
 * other type (a step the API added after this build) opens read only.
 */
export function isKnownNodeType(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(NODE_METADATA, type);
}

/** The state a play holding a step this build doesn't know reads, on its
 *  canvas, on that step and in the step panel. */
export const RELOAD_TO_EDIT = 'Reload to edit this play';

/** Accent colour of a step this build doesn't know (a neutral grey). */
export const UNKNOWN_STEP_COLOR = '#71717A';

/**
 * A step's name: its label, or for a type this build doesn't know (one the
 * API added since), the type in words: `run_play` reads "Run play".
 */
export function nodeLabel(type: string): string {
  const meta = NODE_METADATA[type as CustomNodeType] as NodeMeta | undefined;
  if (meta) return meta.label;
  const words = type.replace(/[_\s-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Step';
}

export function getNodeMeta(type: CustomNodeType): NodeMeta {
  const meta = NODE_METADATA[type] as NodeMeta | undefined;
  if (meta) return meta;
  // A type this build doesn't know (a step newer than the portal) is shown
  // by its name, read only, free and non-branching. Never undefined, so a
  // render never crashes on `undefined.isBranching`.
  return {
    type,
    label: nodeLabel(type),
    description: '',
    category: 'LOGIC',
    defaultConfig: {} as CustomNodeConfig,
    costFn: zero,
    kindIn: 'any',
    kindOut: 'any',
    isBranching: false,
  };
}

/** A fresh, deeply-cloned default config for a node of `type` — never the
 *  shared singleton (mutating a node must not bleed into the metadata table). */
export function nodeDefaultConfig(type: CustomNodeType): CustomNodeConfig {
  return structuredClone(NODE_METADATA[type].defaultConfig);
}

/** Accent colour for a node type. */
export function nodeColor(type: CustomNodeType): string {
  const meta = NODE_METADATA[type] as NodeMeta | undefined;
  return meta ? CATEGORY_COLOR[meta.category] : UNKNOWN_STEP_COLOR;
}

/** The built-in agents' names, for the Agents card's words. */
const BUILTIN_AGENT_NAMES: Record<string, string> = {
  'builtin:research': 'Research',
  'builtin:qualification': 'Qualification',
  'builtin:employment_check': 'Employment check',
};

/** An Agents step's price for one record: its agents at the org's prices,
 *  plus the play's own instructions at the backend's price for them
 *  (`selfPerRecord`, from the validate response). 0 (hidden) until every
 *  agent has a price, so a partial sum never shows. */
function researchCostPerRecord(
  config: ResearchConfig,
  agentPrices: AgentPrices | undefined,
  recordKind: 'person' | 'company',
  selfPerRecord?: number
): number {
  if (hasSelf(config) && selfPerRecord === undefined) return 0;
  let unpriced = false;
  const total = researchCreditsPerRecord(
    config,
    (ids) => {
      const cost = agentsCostPerRecord(ids, agentPrices, recordKind);
      if (cost === undefined) unpriced = true;
      return cost ?? 0;
    },
    selfPerRecord
  );
  return unpriced ? 0 : total;
}

/**
 * Steps whose price only the server knows, per record, from what they load
 * from a vendor: the card reads the validate response's `costs[id]` instead of
 * computing a price (`costFn` is 0), and shows no price until it arrives. A
 * step that loads LinkedIn profile copies or makes lookups shows it as
 * `up to`, since a copy the run already holds, a miss and an outage are free.
 */
export const SERVER_PRICED_STEPS: ReadonlySet<CustomNodeType> = new Set([
  'job_history',
  'identify_person',
  'find_open_jobs',
  'find_reviews',
]);

/** Upper-bound credit cost for `n` records at a node's current config.
 *  `selfPerRecord` is an Agents step's own instructions' price from the
 *  validate response; without it that option shows no price. */
export function nodeCost(
  type: CustomNodeType,
  config: CustomNodeConfig,
  n: number,
  agentPrices?: AgentPrices,
  recordKind?: RecordKind,
  selfPerRecord?: number
): number {
  if (type === 'research' && recordKind === 'company')
    // Research on a company runs at its company scope; the agent's own
    // instructions are priced by the backend, as on people (W2-22).
    return (
      researchCostPerRecord(
        config as ResearchConfig,
        agentPrices,
        'company',
        selfPerRecord
      ) * n
    );
  return getNodeMeta(type).costFn(config, n, agentPrices, selfPerRecord);
}

/** CRM-aware object label (§5.4-H): HubSpot and Attio call an Account a
 *  "Company"; the internal `'Account'` token is presentation-mapped only. */
export function crmObjectLabel(
  crmType: string | undefined,
  objectType: string | undefined
): string {
  if (!objectType) return '';
  if (
    (crmType === 'hubspot' || crmType === 'attio') &&
    objectType === 'Account'
  )
    return 'Company';
  return objectType;
}

/** A play CRM's name. A missing `crmType` is a legacy Salesforce step. Any
 *  other CRM reads as "CRM", never as one of the three. */
export function crmLabel(crmType: PlayCrm | undefined): string {
  if (crmType === undefined) return PLAY_CRM_NAMES.salesforce;
  return isPlayCrm(crmType) ? PLAY_CRM_NAMES[crmType] : 'CRM';
}

/* ── Trigger kinds ───────────────────────────────────────────────────── */

/** Short name for each trigger, used on the node card and in the inspector. */
export const TRIGGER_KIND_LABEL: Record<TriggerKind, string> = {
  deal_stage: 'A deal changes stage',
  deal_stalled: 'A deal stalls',
  sixsense_stage: 'An account reaches a 6sense stage',
  crm_property_change: 'A record matches your conditions',
  crm_list_membership: 'Record joins a list',
  job_change: 'Someone changes jobs',
  new_people: 'New people match your ICP',
  new_companies: 'New companies match your ICP',
  new_jobs: 'Companies start hiring',
  meeting_booked: 'A Google Calendar meeting is booked',
  meeting_starting: 'A Google Calendar meeting is starting',
  meeting_no_notes: 'A Google Calendar meeting has no notes',
  post_engagers: 'People engage with posts',
  tool_adopted: 'Companies start using a tool',
  company_signal: 'Something happens at an account',
  fireflies_call: 'A Fireflies call is recorded',
  fathom_call: 'A Fathom call is recorded',
  gong_call: 'A Gong call is recorded',
  list_leads_added: 'Leads added to a list',
  sheet_rows_added: 'Rows are added to a Google Sheet',
  google_doc_updated: 'A picked Google Doc is updated',
  webhook: 'A webhook arrives',
  play_handoff: 'Another play sends records',
  sequence_event: 'Something happens in a sequence',
  crm_duplicates: 'CRM duplicates',
  notion_page_added: 'A page is added to a Notion database',
  notion_page_updated: 'A page in a Notion database is updated',
  schedule: 'Scheduled brief',
  one_time: 'One-time brief',
};

/** Trigger kinds that watch for a change rather than sweeping on a clock.
 *  These are the ones that get the "from now on / include existing" choice. */
export const CHANGE_TRIGGER_KINDS: TriggerKind[] = [
  'deal_stage',
  'crm_property_change',
  'crm_list_membership',
  'job_change',
  'list_leads_added',
];

export function isChangeTrigger(kind: TriggerKind | undefined): boolean {
  return !!kind && CHANGE_TRIGGER_KINDS.includes(kind);
}

/** First connected Salesforce or HubSpot, falling back to Salesforce. */
function firstPairCrm(
  connectedCrmTypes: readonly string[]
): 'salesforce' | 'hubspot' {
  return (
    (connectedCrmTypes.find((t) => t === 'salesforce' || t === 'hubspot') as
      | 'salesforce'
      | 'hubspot'
      | undefined) ?? 'salesforce'
  );
}

/** First connected CRM of the three. Salesforce and HubSpot come first, so
 *  an org that has either keeps today's default; Attio only when it is the
 *  one connected; Salesforce when none is on. */
export function firstCrm(connectedCrmTypes: readonly string[]): PlayCrm {
  if (connectedCrmTypes.some((t) => t === 'salesforce' || t === 'hubspot'))
    return firstPairCrm(connectedCrmTypes);
  return connectedCrmTypes.includes('attio') ? 'attio' : 'salesforce';
}

/**
 * The starting config for a trigger kind. Every kind narrows to a `source`
 * the engine already polls, so picking one here is what writes the shape the
 * backend reads. Scheduled kinds start weekly, which is the cadence the plays
 * they exist for want; change kinds stay on the 30-minute tick. A CRM
 * duplicates trigger starts from `cleanup`, the org's CRM settings
 * (`useCleanupSeed`), which every picker that offers it passes.
 */
export function triggerConfigForKind(
  kind: TriggerKind,
  connectedCrmTypes: readonly string[] = [],
  previous?: Partial<TriggerConfig>,
  cleanup?: CleanupSeed,
  /** A webhook's format, set by its own row in the trigger dialog: the
   *  mapping starts from the format's (`incomingWebhookPreset`). */
  preset?: NonNullable<IncomingWebhookConfig['preset']>
): TriggerConfig {
  const crmType =
    previous?.jobChange?.crmType ??
    previous?.crm?.crmType ??
    firstCrm(connectedCrmTypes);
  // Job change runs on Salesforce and HubSpot only, so it falls back to the
  // pair. Deal triggers read Attio too.
  const pairCrm =
    crmType === 'attio' ? firstPairCrm(connectedCrmTypes) : crmType;
  const windows = recordWindows(previous);
  // A deal's or a 6sense stage's criteria never carry into a record trigger.
  const ordinaryCrm =
    previous?.crm?.objectType === 'Deal' ||
    previous?.crm?.conditionType === 'sixsense_stage'
      ? undefined
      : previous?.crm;
  const weekly: TriggerSchedule = {
    frequency: 'weekly',
    weekday: 0,
    time: '09:00',
    // Without a zone the engine runs 09:00 UTC while the panel reads local.
    timezone: previous?.schedule?.timezone ?? browserTimezone(),
  };

  switch (kind) {
    case 'deal_stage':
    case 'deal_stalled': {
      const next = dealConfig(previous ?? {}, kind, crmType);
      if (
        kind === 'deal_stalled' &&
        (!next.schedule || next.schedule.frequency === 'continuous')
      )
        next.schedule = weekly;
      return next;
    }
    case 'sixsense_stage':
      // Salesforce only: the 6sense package writes its scores there.
      return {
        ...windows,
        source: 'crm',
        kind,
        crm: {
          crmType: 'salesforce',
          objectType: 'Account',
          conditionType: 'sixsense_stage',
          stages: ['Decision', 'Purchase'],
          backfill: 'now',
        },
        schedule: previous?.schedule,
      };
    case 'fireflies_call':
    case 'fathom_call':
    case 'gong_call': {
      // Each call tool keeps its own settings; the other tool's never carry.
      const tool =
        kind === 'fireflies_call'
          ? 'fireflies'
          : kind === 'gong_call'
            ? 'gong'
            : 'fathom';
      return {
        source: tool,
        kind,
        [tool]: previous?.[tool] ?? {
          who: 'external_participants',
          maxPerMeeting: 5,
          titleContains: [],
          minDurationMinutes: 10,
          backfill: 'now',
        },
        schedule: previous?.schedule,
      };
    }
    case 'sequence_event':
      return {
        source: 'sequence',
        kind,
        sequence: previous?.sequence ?? { events: ['replied', 'finished'] },
      };
    case 'play_handoff':
      return {
        source: 'play',
        kind,
        play: previous?.play ?? { recordKind: 'person' },
      };
    case 'webhook':
      return {
        source: 'webhook',
        kind,
        webhook: preset
          ? previous?.webhook?.preset === preset
            ? previous.webhook
            : incomingWebhookPreset(preset)
          : (previous?.webhook ?? {
              recordKind: 'person',
              identity: {},
              fields: [],
              signature: 'none',
            }),
      };
    case 'google_doc_updated':
      return {
        source: 'google_doc',
        kind,
        googleDoc: previous?.googleDoc ?? { documents: [], backfill: 'now' },
        schedule: previous?.schedule ?? { frequency: 'continuous' },
      };
    case 'sheet_rows_added':
      return {
        source: 'google_sheet',
        kind,
        sheet: { recordKind: 'person', backfill: 'now', ...previous?.sheet },
        schedule: previous?.schedule,
      };
    case 'notion_page_added':
    case 'notion_page_updated':
      return {
        source: 'notion',
        kind,
        notion: {
          recordKind: 'person',
          properties: [],
          identity: {},
          backfill: 'now',
          ...previous?.notion,
        },
        schedule: previous?.schedule,
      };
    case 'list_leads_added':
      return {
        ...windows,
        source: 'lead_list',
        kind,
        leadList: {
          ...previous?.leadList,
          backfill: previous?.leadList?.backfill ?? 'now',
        },
        schedule: previous?.schedule,
      };
    case 'crm_property_change':
      return {
        ...windows,
        source: 'crm',
        kind,
        crm: {
          ...(ordinaryCrm ?? {}),
          crmType,
          objectType: ordinaryCrm?.objectType ?? 'Account',
          conditions: ordinaryCrm?.conditions ?? null,
          conditionType: 'property',
          backfill: ordinaryCrm?.backfill ?? 'now',
        },
        schedule: previous?.schedule,
      };
    case 'crm_list_membership':
      return {
        ...windows,
        source: 'crm',
        kind,
        crm: {
          ...(ordinaryCrm ?? {}),
          crmType,
          objectType: ordinaryCrm?.objectType ?? 'Contact',
          conditions: ordinaryCrm?.conditions ?? null,
          conditionType: 'list_membership',
          backfill: ordinaryCrm?.backfill ?? 'now',
        },
        schedule: previous?.schedule,
      };
    case 'job_change':
      return {
        source: 'job_change',
        kind,
        jobChange: {
          crmType: pairCrm,
          scope: previous?.jobChange?.scope ?? 'all',
          listId: previous?.jobChange?.listId ?? null,
          listName: previous?.jobChange?.listName ?? null,
          ...(previous?.jobChange?.scope === 'filter' &&
          previous.jobChange.conditions
            ? { conditions: previous.jobChange.conditions }
            : {}),
          ...(previous?.jobChange?.scope === 'reports' &&
          previous.jobChange.reports
            ? { reports: previous.jobChange.reports }
            : {}),
          watch: previous?.jobChange?.watch ?? { company: true, title: false },
          freshness: previous?.jobChange?.freshness ?? 'monthly',
        },
        schedule: { frequency: 'continuous' },
      };
    case 'company_signal':
      return {
        source: 'company_signal',
        kind,
        companySignal: previous?.companySignal ?? {
          accounts: { domains: [] },
          signals: ['job_posting'],
          freshness: 'monthly',
        },
        schedule: previous?.schedule ?? weekly,
      };
    case 'meeting_booked':
    case 'meeting_starting':
    case 'meeting_no_notes': {
      const event = kind.slice('meeting_'.length) as
        | 'booked'
        | 'starting'
        | 'no_notes';
      return {
        source: 'calendar',
        kind,
        calendar: {
          googleUserIds: previous?.calendar?.googleUserIds ?? [],
          event,
          hoursBefore:
            event === 'starting'
              ? (previous?.calendar?.hoursBefore ?? 2)
              : null,
          who: 'external_attendees',
          maxPerMeeting: previous?.calendar?.maxPerMeeting ?? 5,
          titleContains: previous?.calendar?.titleContains ?? [],
        },
        schedule: { frequency: 'continuous' },
      };
    }
    case 'tool_adopted':
      return {
        source: 'crustdata_watch',
        kind,
        watch: {
          toolFilters: previous?.watch?.toolFilters ?? {
            technology: '',
            include_existing: false,
          },
          companyFilters: previous?.watch?.companyFilters ?? {
            ...INITIAL_COMPANY_FILTERS,
          },
          perPeriod: previous?.watch?.perPeriod ?? 100,
        },
        schedule: previous?.schedule ?? weekly,
      };
    case 'post_engagers':
      return {
        source: 'cleanlist',
        kind,
        cleanlist: {
          kind: 'posts',
          postFilters: previous?.cleanlist?.postFilters ?? {
            posted_within_days: 14,
          },
          peopleFilters: previous?.cleanlist?.peopleFilters,
          perPeriod: previous?.cleanlist?.perPeriod ?? 100,
        },
        schedule: previous?.schedule ?? weekly,
      };
    case 'new_jobs':
      return {
        ...windows,
        source: 'cleanlist',
        kind,
        cleanlist: {
          kind: 'jobs',
          jobFilters: previous?.cleanlist?.jobFilters ?? {
            posted_within_days: 21,
            exclude_reposts: true,
          },
          perPeriod: previous?.cleanlist?.perPeriod ?? 100,
        },
        schedule: previous?.schedule ?? weekly,
      };
    case 'new_people':
    case 'new_companies':
      return {
        ...windows,
        source: 'cleanlist',
        kind,
        cleanlist: {
          ...(previous?.cleanlist ?? {}),
          kind: kind === 'new_companies' ? 'company' : 'people',
          maxPerCycle: previous?.cleanlist?.maxPerCycle ?? 100,
        },
        schedule: previous?.schedule ?? weekly,
      };
    case 'schedule':
    case 'one_time':
      // No picker row offers a brief (the Triggers tab and Clu create them).
      return { source: 'schedule', kind, schedule: defaultBriefSchedule(kind) };
    case 'crm_duplicates':
      // The seed's CRM. The rules start from the design 2.4 defaults under
      // the org's settings: a match key the org dropped is off, and a Lead
      // org checks its Leads. A weekly, monthly or quarterly schedule carries
      // over, anything else starts quarterly.
      return cleanupTriggerConfig(cleanup?.crmType ?? CLEANUP_CRM_TYPES[0], {
        matchKeys: cleanup?.matchKeys,
        recordType: cleanup?.recordType,
        schedule: previous?.schedule,
      });
  }
}

/** `text` with its first letter capitalized when its first word is all
 *  lowercase ("tax manager" reads "Tax manager"); a word with a capital of
 *  its own stays as typed ("iOS", "eClinicalWorks"). Mirrors the API's
 *  `_capitalized`. */
function capitalized(text: string): string {
  const first = text.split(' ', 1)[0];
  return first === first.toLowerCase()
    ? text.slice(0, 1).toUpperCase() + text.slice(1)
    : text;
}

/** First two values, capitalized, then "+N": "Staff accountant, Tax
 *  associate +4". Mirrors the API's `_first_and_more`. */
function firstAndMore(values: readonly string[], shown = 2): string | null {
  const kept = values.filter(Boolean);
  if (!kept.length) return null;
  const head = kept.slice(0, shown).map(capitalized).join(', ');
  const rest = kept.length - shown;
  return rest > 0 ? `${head} +${rest}` : head;
}

// A stored country (ISO-3 code, full name or display name) reads as its
// display name, and the two the cards name most read short.
const COUNTRY_DISPLAY = new Map<string, string>(
  COUNTRIES.flatMap(({ iso3, full, display }): [string, string][] => [
    [iso3, display],
    [full, display],
  ])
);
const COUNTRY_SHORT = new Map([
  ['United States', 'US'],
  ['United Kingdom', 'UK'],
]);

function countryWord(name: string): string {
  const display = COUNTRY_DISPLAY.get(name) ?? name;
  return COUNTRY_SHORT.get(display) ?? display;
}

/**
 * The job trigger card's line, worded as the API words it for Clu
 * (`job_audience_words`, parity pinned by `fixtures/job_line_parity.json`):
 * "Tax technology, Tax innovation +1 or posts name UltraTax · Accounting ·
 * 11-50 · US, Canada". Titles, description phrases and technologies are
 * alternatives, so they join with "or"; the other filters narrow them. A
 * line that starts on the phrases reads "Posts name UltraTax".
 */
export function jobAudienceWords(
  filters: JobFilters | null | undefined
): string {
  const parts: string[] = [];
  const titles = firstAndMore(filters?.job_titles ?? []);
  const phrases = firstAndMore(filters?.description_phrases ?? []);
  const tools = firstAndMore(filters?.technologies ?? []);
  const posting = [titles, phrases && `posts name ${phrases}`, tools].filter(
    Boolean
  );
  if (posting.length) {
    // "Posts name UltraTax" when no title comes first: sentence case.
    parts.push(capitalized(posting.join(' or ')));
  }
  for (const values of [filters?.industries, filters?.company_sizes]) {
    const words = firstAndMore(values ?? []);
    if (words) parts.push(words);
  }
  const countries = [...new Set((filters?.countries ?? []).map(countryWord))];
  const where = firstAndMore(countries, 3);
  if (where) parts.push(where);
  return parts.join(' · ') || 'Add job filters';
}

/** Cadence in a few words for a card summary, where the weekday and the
 *  time do not fit and are a click away in the panel: "Weekly",
 *  "Monthly, 15th", "Monthly, 1st business day", "Quarterly". */
export function scheduleShort(
  schedule: TriggerSchedule | undefined
): string | null {
  if (!schedule || schedule.frequency === 'continuous') return null;
  switch (schedule.frequency) {
    case 'daily':
      return 'Daily';
    case 'weekdays':
      return 'Weekdays';
    case 'every_n_days':
      return `Every ${schedule.everyNDays ?? '?'} days`;
    case 'weekly':
      return 'Weekly';
    case 'monthly':
      if (schedule.firstBusinessDay) return 'Monthly, 1st business day';
      if (schedule.dayOfMonth === LAST_DAY_OF_MONTH) return 'Monthly, last day';
      return schedule.dayOfMonth
        ? 'Monthly, ' + ordinal(schedule.dayOfMonth)
        : 'Monthly';
    case 'quarterly':
      return 'Quarterly';
    case 'once': {
      const day = schedule.date
        ? parse(schedule.date, 'yyyy-MM-dd', new Date())
        : null;
      return day && isValid(day) ? 'Once, ' + format(day, 'MMM d') : 'Once';
    }
    default:
      return null;
  }
}

/**
 * The line a card leads with, the way a sequence step leads with its title.
 * The trigger says what starts the play; every other step says what it is
 * configured to do, falling back to its own name before it is set up. A
 * tool's step leads with the tool ("Salesloft · Add to cadence", "Slack ·
 * #sales").
 */
export function nodeHeadline(
  type: CustomNodeType,
  config: CustomNodeConfig,
  /** The play's trigger, for steps whose words follow what it checks. */
  trigger?: Partial<TriggerConfig>
): string {
  if (type === 'trigger') {
    const kind = triggerKind(config as Partial<TriggerConfig>);
    if (
      kind === 'crm_property_change' &&
      hasCrmChangeConditions((config as TriggerConfig).crm?.conditions)
    )
      return 'A field changes';
    return kind ? TRIGGER_KIND_LABEL[kind] : 'Pick a trigger';
  }
  return (
    connectorHeadline(type, config, trigger) ??
    nodeSummary(type, config, trigger) ??
    nodeLabel(type)
  );
}

/** The second line on a card: config detail for the trigger, Enrich and Notify
 *  delivery, the step's own description otherwise. A tool's step never shows
 *  its type's description: `connectorDetail` gives its settings or its
 *  action. */
export function nodeDetail(
  type: CustomNodeType,
  config: CustomNodeConfig,
  showCrmFields = true,
  /** The play's trigger, for the CRM Review and Merge duplicates work on. */
  trigger?: Partial<TriggerConfig>
): string | null {
  if (
    type === 'trigger' &&
    (config as Partial<TriggerConfig>).source === 'schedule'
  ) {
    // A brief's card leads with its kind, so its line is when it runs.
    const schedule = (config as Partial<TriggerConfig>).schedule;
    return (
      [
        scheduleShort(schedule),
        schedule?.time && formatClock(schedule.time),
        schedule?.timezone && zoneCity(schedule.timezone) + ' time',
      ]
        .filter(Boolean)
        .join(' · ') || 'Pick when it runs'
    );
  }
  if (type === 'trigger')
    return nodeSummary(type, config, undefined, showCrmFields);
  if (type === 'enrich' && (config as EnrichConfig).target === 'company')
    return 'Enrich company firmographics';
  if (
    type === 'crm_action' &&
    (config as CrmActionConfig).objectType === 'Account'
  )
    return (config as CrmActionConfig).action === 'lookup'
      ? 'Read company fields from your CRM'
      : 'Sync company fields to your CRM';
  if (type === 'notify') {
    const deliver = (config as NotifyConfig).deliver;
    if (deliver === 'per_record') return 'Send one message for each record';
    if (deliver === 'list') return 'Send records together in a digest';
  }
  if (type === 'export' && (config as ExportConfig).destination === 'lead_list')
    return (
      ((config as ExportConfig).columns ?? [])
        .map((column) => column.header)
        .join(', ') || 'Pick columns'
    );
  if (
    type === 'export' &&
    (config as ExportConfig).destination === 'company_list'
  )
    return null;
  const connector = connectorDetail(type, config, trigger);
  if (connector) return connector;
  return (NODE_METADATA[type] as NodeMeta | undefined)?.description ?? null;
}

/**
 * A short, at-a-glance summary of a node's CURRENT config, shown on the card
 * so a step reads as "Email + Phone" rather than a generic blurb.
 */
export function nodeSummary(
  type: CustomNodeType,
  config: CustomNodeConfig,
  /** The play's trigger: Review duplicates says records once it checks leads. */
  trigger?: Partial<TriggerConfig>,
  showCrmFields = true
): string | null {
  switch (type) {
    case 'trigger': {
      const c = config as Partial<TriggerConfig>;
      const cadence = scheduleShort(c.schedule);
      if (
        c.source === 'fireflies' ||
        c.source === 'fathom' ||
        c.source === 'gong'
      ) {
        const tool = { fireflies: 'Fireflies', fathom: 'Fathom', gong: 'Gong' }[
          c.source
        ];
        const people =
          c[c.source]?.who === 'first_external_participant'
            ? 'First outside participant'
            : 'Outside participants';
        return [`${tool} calls`, people, cadence].filter(Boolean).join(' · ');
      }
      if (c.source === 'calendar') {
        const count = c.calendar?.googleUserIds.length ?? 0;
        const event = c.calendar?.event;
        const when =
          event === 'starting'
            ? `${c.calendar?.hoursBefore ?? 2} hours before a meeting`
            : event === 'no_notes'
              ? 'No notes two hours after a meeting'
              : 'A meeting is booked';
        return `Google Calendar · ${when} · ${count ? `${count} calendar${count === 1 ? '' : 's'}` : 'Pick calendars'}`;
      }
      if (c.source === 'sequence') {
        const events = c.sequence?.events ?? [];
        // Two events read in words; more are counted.
        const words =
          events.length > 2
            ? `${events.length} events`
            : SEQUENCE_EVENT_OPTIONS.filter((option) =>
                events.includes(option.value)
              )
                .map((option) => option.word)
                .join(' or ');
        return [c.sequence?.sequenceName || 'Pick a sequence', words]
          .filter(Boolean)
          .join(' · ');
      }
      if (c.source === 'play')
        return c.play?.recordKind === 'company'
          ? 'Receive company records from this agent’s plays'
          : 'Receive person records from this agent’s plays';
      if (c.source === 'webhook')
        return c.webhook?.recordKind === 'company'
          ? 'Incoming company records'
          : 'Incoming person records';
      if (c.source === 'notion')
        return [notionTriggerSummary(c.kind, c.notion), cadence]
          .filter(Boolean)
          .join(' · ');
      if (c.source === 'google_sheet')
        return [
          (c.sheet?.scope === 'all_rows' ? 'Every row in ' : 'New rows in ') +
            (c.sheet?.spreadsheetName || 'a Google Sheet'),
          c.sheet?.sheetName,
          cadence,
        ]
          .filter(Boolean)
          .join(' · ');
      if (c.source === 'google_doc')
        return [googleDocTriggerSummary(c.googleDoc), cadence]
          .filter(Boolean)
          .join(' · ');
      if (c.source === 'lead_list')
        return [c.leadList?.listName || 'Pick a list', cadence]
          .filter(Boolean)
          .join(' · ');
      const withCadence = (base: string) =>
        cadence ? `${base} · ${cadence}` : base;
      if (triggerKind(c) === 'sixsense_stage') {
        const crm = c.crm;
        const product = crm?.product?.trim();
        return withCadence(
          [
            'Salesforce',
            crm?.stages?.length
              ? `6sense ${crm.stages.join(', ')}`
              : 'Pick a 6sense stage',
            crm?.minIntent != null && `score ${crm.minIntent}+`,
            product,
          ]
            .filter(Boolean)
            .join(' · ')
        );
      }
      if (isDealKind(triggerKind(c))) {
        const crm = c.crm;
        const stages = crm?.stages?.length
          ? `${crm.stages.length} selected ${crm.stages.length === 1 ? 'stage' : 'stages'}`
          : c.kind === 'deal_stalled'
            ? 'Any open stage'
            : 'Pick a stage';
        return withCadence(
          `${crmLabel(crm?.crmType)} deals · ${stages}${c.kind === 'deal_stalled' ? ` · ${crm?.stalledDays ?? 14} days without activity` : ''}${crm?.amountMin != null ? ` · Amount ≥ ${crm.amountMin}` : ''}`
        );
      }
      if (triggerKind(c) === 'job_change') {
        const jc = c.jobChange;
        const crm = crmLabel(jc?.crmType);
        const conditions = jc?.conditions?.conditions?.length ?? 0;
        // Each picked report by its relationship, in priority order.
        const reports = (Array.isArray(jc?.reports) ? jc.reports : [])
          .filter((report) => report?.id)
          .map(
            (report) =>
              report.relationship?.trim() || report.name?.trim() || report.id
          );
        const where =
          jc?.scope === 'list'
            ? jc.listName
              ? `${crm} list: ${jc.listName}`
              : `Pick a ${crm} list`
            : jc?.scope === 'filter'
              ? conditions
                ? `${crm} contacts matching ${conditions} ${conditions === 1 ? 'condition' : 'conditions'}`
                : `Add ${crm} contact conditions`
              : jc?.scope === 'reports'
                ? reports.length
                  ? `Salesforce reports: ${reports.join(', ')}`
                  : 'Pick Salesforce reports'
                : `All ${crm} contacts`;
        const moves = [
          jc?.watch?.company && 'new company',
          jc?.watch?.title && 'new title',
        ].filter(Boolean);
        return `${moves.length ? `${where} · ${moves.join(' or ')}` : where} · ${jc?.freshness === 'biweekly' ? 'Every 2 weeks, 10 credits per move' : 'Monthly, 5 to 10 credits per move'}`;
      }
      if (c.source === 'crm' && c.crm) {
        const fields =
          showCrmFields && Array.isArray(c.crm.fields) && c.crm.fields.length
            ? `${c.crm.fields.length} fields`
            : '';
        // W1-32: all matches each period shows as the trigger's state.
        const scope =
          c.crm.scope === 'all_matches'
            ? typeof c.crm.perPeriod === 'number'
              ? `Up to ${c.crm.perPeriod.toLocaleString('en-US')} each period`
              : 'All matches each period'
            : '';
        const withFields = (summary: string) =>
          withCadence([summary, scope, fields].filter(Boolean).join(' · '));
        const object = crmObjectLabel(c.crm.crmType, c.crm.objectType);
        if (c.crm.conditionType === 'list_membership') {
          // Kind-aware (§2f): "SF report: Q3 inbound" reads what's actually
          // watched. listKind is SF-only; missing ⇒ campaign (back-compat).
          const name = c.crm.listName || 'pick a list';
          const crm = c.crm.crmType ?? 'salesforce';
          if (crm === 'attio') return withFields(`Attio list: ${name}`);
          if (crm !== 'salesforce' && crm !== 'hubspot')
            return withFields(`List: ${name}`);
          if (crm === 'salesforce') {
            const kind = c.crm.listKind ?? 'campaign';
            const kindLabel =
              kind === 'list_view'
                ? 'list view'
                : kind === 'report'
                  ? 'report'
                  : 'campaign';
            return withFields(`SF ${kindLabel}: ${name}`);
          }
          // An agent's play (`showCrmFields`) on a HubSpot people list says
          // which members it picks up: historic, else now (the default, also
          // when no choice is saved).
          const members =
            showCrmFields &&
            c.crm.objectType !== 'Account' &&
            c.crm.scope !== 'all_matches'
              ? c.crm.backfill === 'historic'
                ? 'Existing and new members'
                : 'New members'
              : '';
          return withFields(
            [`HubSpot list: ${name}`, members].filter(Boolean).join(' · ')
          );
        }
        return withFields(
          `${crmLabel(c.crm.crmType)} ${object} ${
            hasCrmChangeConditions(c.crm.conditions)
              ? 'field changes'
              : 'matches'
          }`
        );
      }
      if (c.source === 'company_signal') {
        const names = c.companySignal?.signals
          .map(
            (signal) =>
              ({
                job_posting: 'Hiring',
                news: 'News',
                funding: 'Funding',
                headcount: 'Headcount',
              })[signal]
          )
          .join(', ');
        return withCadence(
          names ? `Account events: ${names}` : 'Select account events'
        );
      }
      if (c.source === 'crustdata_watch') {
        return withCadence(
          c.watch?.toolFilters.technology
            ? `Adopts ${c.watch.toolFilters.technology}`
            : 'Select a tool'
        );
      }
      if (c.source === 'cleanlist' && c.cleanlist) {
        if (c.cleanlist.kind === 'posts') {
          const filters = c.cleanlist.postFilters;
          const what =
            filters?.text_phrases?.join(', ') ||
            (filters?.author_urls?.length
              ? `${filters.author_urls.length} post authors`
              : 'Set post filters');
          return withCadence(`Post engagement: ${what}`);
        }
        if (c.cleanlist.kind === 'jobs')
          return withCadence(jobAudienceWords(c.cleanlist.jobFilters));
        return withCadence(
          c.cleanlist.kind === 'company'
            ? `${BRAND.name} company search`
            : `${BRAND.name} people search`
        );
      }
      if (c.source === 'crm_duplicates') {
        return withCadence(cleanupScopeWords(c));
      }
      // An agent's scheduled brief (brief.util): kind schedule or one_time.
      if (c.source === 'schedule')
        return (c.kind as BriefKind | undefined) === 'one_time'
          ? 'One-time brief'
          : 'Scheduled brief';
      return 'Pick a trigger source';
    }
    case 'enrich': {
      const c = config as EnrichConfig;
      if (c.target === 'company') return 'Company firmographics';
      const p = c.person ?? { profile: false, email: false, phone: false };
      const parts = [
        p.email && 'Email',
        p.phone && (p.phoneMode === 'mobile' ? 'Mobile only' : 'Phone'),
        p.profile && 'Profile',
        p.personalEmail === true && 'Personal email',
        p.reverseLookup === true && 'From email',
      ].filter(Boolean);
      return parts.length ? parts.join(' + ') : 'Nothing selected';
    }
    case 'fireflies_notes':
    case 'fathom_notes':
    case 'gong_notes':
      return callNotesSummary(config as CallNotesConfig);
    case 'find_open_jobs':
    case 'find_reviews':
      return NODE_METADATA[type].label;
    case 'get_company':
      return `Get company · ${(config as GetCompanyConfig).source === 'crm_account' ? 'CRM account' : (config as GetCompanyConfig).source === 'employer' ? 'Employer' : 'Pick a source'}`;
    case 'find_people':
      return `Up to ${(config as FindPeopleConfig).maxPeoplePerAccount}/company`;
    case 'research': {
      const research = config as ResearchConfig;
      const ids = research.agentIds ?? [];
      const n = ids.length;
      const rest = ids.filter((id) => id !== SELF_AGENT_ID);
      let restWords: string | null = null;
      if (rest.includes(WRITE_SKILL_ID)) {
        // "Qualification, Write (first line)", custom agents counted.
        const named = rest.flatMap((id) =>
          id === WRITE_SKILL_ID
            ? [writeCardLabel(research.write?.kind)]
            : BUILTIN_AGENT_NAMES[id]
              ? [BUILTIN_AGENT_NAMES[id]]
              : []
        );
        const others = rest.length - named.length;
        restWords = [
          ...named,
          ...(others ? [`${others} agent${others === 1 ? '' : 's'}`] : []),
        ].join(', ');
      }
      if (hasSelf(research)) {
        const words = selfSummary(research.self);
        if (restWords) return `${words} · ${restWords}`;
        const others = rest.length;
        return others
          ? `${words} · ${others} more agent${others === 1 ? '' : 's'}`
          : words;
      }
      if (restWords) return restWords;
      return n ? `${n} agent${n === 1 ? '' : 's'}` : 'No agents yet';
    }
    case 'validate': {
      const c = config as ValidateConfig;
      const parts = [c.email && 'email', c.phone && 'phone'].filter(Boolean);
      return parts.length ? `Verify ${parts.join(' + ')}` : 'Nothing selected';
    }
    case 'crm_action': {
      const c = config as CrmActionConfig;
      if (isCrmWriteAction(c.action)) return crmWriteSummary(c);
      const object = crmObjectLabel(c.crmType, c.objectType);
      const base = `${c.action === 'lookup' ? 'Look up' : c.target === 'new_record' ? 'Create new in' : 'Sync to'} ${crmLabel(c.crmType)} ${object}`;
      // R10 (§2e): non-default owner assignment is visible at a glance.
      const owner = c.action === 'sync' ? syncOwnerWords(c.assignment) : null;
      return owner ? `${base} · ${owner}` : base;
    }
    case 'add_to_sequence':
      return sequenceStepSummary(config as AddToSequenceConfig);
    case 'find_meetings': {
      const c = config as FindMeetingsConfig;
      const count = Array.isArray(c.googleUserIds) ? c.googleUserIds.length : 0;
      const who = c.matchBy === 'company' ? 'Company' : 'Contact';
      return `${who} · ${count ? `${count} calendar${count === 1 ? '' : 's'}` : 'Pick calendars'}`;
    }
    case 'job_history':
      return jobHistorySummary(config as JobHistoryConfig);
    case 'match_company':
      return matchCompanySummary(config as MatchCompanyConfig);
    case 'count_people':
      // The card reads its name over its description. Whether the audience
      // counts anyone is the step's issue (graph-ops countPeopleIssue, on the
      // wire the API reads), so the card and inspector never disagree.
      return null;
    case 'instantly_capacity':
      // No settings: the card reads its name over its description.
      return null;
    case 'agent_task': {
      const tools = agentTaskTools(config as AgentTaskConfig);
      return tools.length
        ? 'Reads ' + tools.map((tool) => AGENT_TASK_TOOLS[tool]).join(', ')
        : 'Pick what it reads';
    }
    case 'export': {
      const c = config as ExportConfig;
      if (c.destination === 'lead_list') return 'Write to list columns';
      if (c.destination === 'company_list')
        return c.listName ? `Add to ${c.listName}` : 'Add companies to a list';
      if (!c.spreadsheetId) return 'Create a sheet';
      if (c.mode === 'upsert' && c.spreadsheetName)
        return `Update rows in ${c.spreadsheetName}`;
      return c.spreadsheetName
        ? `Export to ${c.spreadsheetName}`
        : 'Export to Google Sheets';
    }
    case 'create_google_doc':
      return createGoogleDocSummary(config as CreateGoogleDocConfig);
    case 'send_mailbox_email':
      return sendMailboxEmailSummary(config as SendMailboxEmailConfig);
    case 'send_linkedin_action':
      return sendLinkedInActionSummary(config as SendLinkedInActionConfig);
    case 'approval': {
      const c = config as ApprovalConfig;
      return c.approvers?.mode === 'record_owner'
        ? 'Record owner'
        : c.approvers?.mode === 'users'
          ? `${c.approvers.userIds?.length ?? 0} people`
          : 'Play owner';
    }
    case 'branch': {
      const arms = (config as BranchConfig).branches ?? [];
      const condition = arms.find((arm) =>
        extendedBranchConditions(arm.conditions)
      );
      return condition
        ? summarizeBranchConditions(condition.conditions)
        : branchRouteSummary(config as BranchConfig);
    }
    case 'notify': {
      const c = config as NotifyConfig;
      const mode =
        c.deliver === 'list'
          ? 'Digest · '
          : c.deliver === 'per_record'
            ? 'Each record · '
            : '';
      if (c.channel === 'teams') {
        if (c.webhookId) return `Teams · ${c.channelName || c.webhookId}`;
        if (c.to === 'play_owner')
          return (
            mode +
            (c.memberId !== undefined
              ? 'Teammate’s Teams DM'
              : 'Agent owner’s Teams DM')
          );
        if (c.to === 'record_owner') return mode + 'Record owner’s Teams DM';
        return c.teamsChannelId
          ? `${mode}Teams · ${c.channelName || c.teamsChannelId}`
          : 'Connect a Teams channel';
      }
      if (c.to === 'record_owner') return mode + 'Record owner';
      if (c.to === 'play_owner')
        return (
          mode +
          (c.memberId !== undefined
            ? 'Teammate’s Clu DM'
            : 'Agent owner’s Clu DM')
        );
      if (c.channel === 'email')
        return mode + (mode ? 'Email · recipients' : 'Email · playbook owner');
      return c.channelId
        ? `${mode}Slack · #${c.channelName || c.channelId}`
        : 'Connect a Slack channel';
    }
    case 'run_play': {
      const c = config as HandOffConfig;
      return `Hand off · ${c.playName || c.playbookId || 'Pick a play'}`;
    }
    case 'code': {
      const count = (config as CodeConfig).outputs?.fields.length ?? 0;
      return `${count} output${count === 1 ? '' : 's'}`;
    }
    case 'wait':
      return waitSummary(config as WaitConfig);
    case 'webhook': {
      const c = config as WebhookConfig;
      const host = webhookHost(c.url);
      return host
        ? `${host} · ${c.mode === 'batch' ? 'batches' : 'each record'}`
        : 'Choose an https endpoint';
    }
    case 'review_duplicates':
      return reviewPolicyLabel(
        (config as Partial<ReviewDuplicatesConfig>).policy,
        trigger
      );
    case 'merge_duplicates':
      return mergeFieldsWords(config as Partial<MergeDuplicatesConfig>);
    case 'identify_person':
      return identifyPersonSummary();
    case 'notion_add_page':
    case 'notion_update_page':
      return notionStepSummary(type, config as Partial<NotionStepConfig>);
    default:
      return null;
  }
}
